import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import EventIcon from '@mui/icons-material/Event'
import GridViewIcon from '@mui/icons-material/GridView'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import MapIcon from '@mui/icons-material/Map'
import PhoneIcon from '@mui/icons-material/Phone'
import ScienceIcon from '@mui/icons-material/Science'
import Search from '@mui/icons-material/Search'
import API from '../../services/API'
import { UserContext } from '../../App'
import EmptyState from '../../components/EmptyState/EmptyState'
import BigModal from '../../components/Modals/BigModal'
import UpgradeRequired from '../../components/Upgrade/UpgradeGate'
import BaseLocationForm from './BaseLocationForm'
import BaseLocationsMap from './BaseLocationsMap'
import planAllowances from '../../helpers/planAllowances.json'

// Figures come from the plan catalogue mirror, never typed in here.
const premiumBaseLocations =
  planAllowances.plans.premium.allowances.baseLocations
const enterpriseBaseLocations =
  planAllowances.plans.enterprise.allowances.baseLocations

// Rendered inside a `display: flex` Grid item so the tile stretches to the
// row height — don't give it a percentage height: a percent-height child
// directly under a spaced MuiGrid item resolves against the wrong box and
// overflows into the next row.
const StatTile = ({ label, value, caption }) => (
  <Card variant="outlined" sx={{ p: 1.5, width: '100%' }}>
    <Typography variant="body2" color="text.secondary" noWrap>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    )}
  </Card>
)

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')

const LocationCard = ({ base }) => {
  const navigate = useNavigate()
  const counts = base.counts || {}

  return (
    <Card
      variant="outlined"
      sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Stack spacing={1.25} flex={1}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              bgcolor: 'anBlue.main',
              fontWeight: 700,
              width: 44,
              height: 44,
            }}
          >
            {getInitials(base.name)}
          </Avatar>
          <Box minWidth={0} flex={1}>
            <Typography sx={{ fontWeight: 800 }} noWrap>
              {base.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[base.address?.town, base.address?.postCode]
                .filter(Boolean)
                .join(' · ') || '—'}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2.5} alignItems="center">
          <Box>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {counts.vehicles ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              vehicles
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {counts.staff ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              staff
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {counts.rosters ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              rosters
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {counts.upcomingEvents > 0 && (
            <Chip
              size="small"
              icon={<EventIcon />}
              label={`${counts.upcomingEvents} upcoming event${
                counts.upcomingEvents === 1 ? '' : 's'
              }`}
              color="info"
              sx={{ borderRadius: 1, fontWeight: 700 }}
            />
          )}
          {counts.clients > 0 && (
            <Chip
              size="small"
              label={`${counts.clients} client${
                counts.clients === 1 ? '' : 's'
              }`}
              variant="outlined"
              sx={{ borderRadius: 1, fontWeight: 700 }}
            />
          )}
          <Chip
            size="small"
            icon={<ScienceIcon />}
            label={`${counts.coshhSheets ?? 0} COSHH sheet${
              counts.coshhSheets === 1 ? '' : 's'
            }`}
            variant="outlined"
            color={counts.coshhSheets > 0 ? 'success' : 'default'}
            sx={{ borderRadius: 1, fontWeight: 700 }}
            onClick={() => navigate(`${base._id}?opentab=coshh`)}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mt: 'auto' }}
        >
          <Button
            size="small"
            onClick={() => navigate(base._id)}
            variant="outlined"
            color="contrast"
          >
            Open
          </Button>
          <Box flex={1} />
          {base.onCallNumber && (
            <Tooltip title={`On call: ${base.onCallNumber}`}>
              <IconButton size="small" href={`tel:${base.onCallNumber}`}>
                <PhoneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}

const BaseLocationsDashboard = () => {
  const { user } = useContext(UserContext)
  const isSiteAdmin = !!user?.security?.isSiteAdmin
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [locations, setLocations] = useState([])
  const [coshhTotal, setCoshhTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('name')
  const [view, setView] = useState('cards')
  const [openAdd, setOpenAdd] = useState(false)
  const [limitInfo, setLimitInfo] = useState({})
  const limitReached =
    limitInfo?.limit !== undefined && limitInfo.used >= limitInfo.limit

  const fetchDashboard = () => {
    setLoading(true)
    setFetchError(false)
    API.get('/base-locations/dashboard')
      .then((res) => {
        if (res.status === 200) {
          setLocations(res.data.locations || [])
          setCoshhTotal(res.data.coshhTotal || 0)
        } else {
          setFetchError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }

  const fetchLimit = () => {
    if (!isSiteAdmin) return
    API.get('/subscription/usage').then((res) => {
      if (res.status === 200) setLimitInfo(res.data.baseLocations || {})
    })
  }

  useEffect(() => {
    fetchDashboard()
    fetchLimit()
  }, [])

  const totals = useMemo(
    () => ({
      locations: locations.length,
      vehicles: locations.reduce(
        (sum, base) => sum + (base.counts?.vehicles || 0),
        0,
      ),
      upcomingEvents: locations.reduce(
        (sum, base) => sum + (base.counts?.upcomingEvents || 0),
        0,
      ),
    }),
    [locations],
  )

  const visibleLocations = useMemo(() => {
    const query = search.trim().toLowerCase()
    const matches = (value) => value?.toLowerCase().includes(query)
    const filtered = locations.filter(
      (base) =>
        !query ||
        matches(base.name) ||
        matches(base.address?.town) ||
        matches(base.address?.postCode),
    )
    const sorters = {
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      vehicles: (a, b) =>
        (b.counts?.vehicles || 0) - (a.counts?.vehicles || 0),
      staff: (a, b) => (b.counts?.staff || 0) - (a.counts?.staff || 0),
      upcomingEvents: (a, b) =>
        (b.counts?.upcomingEvents || 0) - (a.counts?.upcomingEvents || 0),
    }
    return [...filtered].sort(sorters[sortBy] || sorters.name)
  }, [locations, search, sortBy])

  const handleSaved = () => {
    setOpenAdd(false)
    fetchDashboard()
    fetchLimit()
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Typography variant="title2">Base Locations</Typography>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(e, next) => next && setView(next)}
            >
              <ToggleButton value="cards" aria-label="Cards">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="map" aria-label="Map">
                <MapIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            {isSiteAdmin && (
              <Button
                size="small"
                variant="contained"
                color="anBlue"
                startIcon={<AddIcon />}
                onClick={() => setOpenAdd(true)}
              >
                Add Location
              </Button>
            )}
          </Stack>
        </Grid>

        {loading && (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        )}
        {fetchError && (
          <Grid item xs={12}>
            <Alert severity="error" variant="filled">
              Unable to load Base Locations. Please try again or contact
              AmbuNet support.
            </Alert>
          </Grid>
        )}

        {!loading && !fetchError && locations.length > 0 && (
          <>
            <Grid item xs={6} sm={3} display="flex">
              <StatTile
                label="Base locations"
                value={totals.locations}
                caption={
                  isSiteAdmin && limitInfo?.limit !== undefined
                    ? `${limitInfo.used ?? totals.locations} of ${limitInfo.limit} on your plan`
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={6} sm={3} display="flex">
              <StatTile
                label="Vehicles"
                value={totals.vehicles}
                caption="Assigned to a base"
              />
            </Grid>
            <Grid item xs={6} sm={3} display="flex">
              <StatTile
                label="Upcoming events"
                value={totals.upcomingEvents}
                caption="Booked in from today"
              />
            </Grid>
            <Grid item xs={6} sm={3} display="flex">
              <StatTile
                label="COSHH data sheets"
                value={coshhTotal}
                caption="Across all locations"
              />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                component={Paper}
                padding={2}
              >
                <TextField
                  size="small"
                  placeholder="Search base locations..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoComplete="none"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  select
                  size="small"
                  label="Sort by"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ minWidth: 180, flex: 0.3 }}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="vehicles">Most vehicles</MenuItem>
                  <MenuItem value="staff">Most staff</MenuItem>
                  <MenuItem value="upcomingEvents">Upcoming events</MenuItem>
                </TextField>
              </Stack>
            </Grid>
          </>
        )}

        {!loading && !fetchError && locations.length === 0 && (
          <Grid item xs={12}>
            <EmptyState
              icon={LocationCityIcon}
              title="No base locations yet"
              description="Base locations power rostering, fleet assignment, PTS pricing and COSHH storage. Add your first base to get started."
              actions={
                isSiteAdmin
                  ? [{ label: 'Add a base location', onClick: () => setOpenAdd(true) }]
                  : undefined
              }
            />
          </Grid>
        )}

        {!loading &&
          !fetchError &&
          locations.length > 0 &&
          visibleLocations.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">
                No base locations found{search ? ' for this search' : ''}.
              </Alert>
            </Grid>
          )}

        {view === 'map' && visibleLocations.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <BaseLocationsMap
                locations={visibleLocations}
                canEdit={isSiteAdmin}
                onSaved={fetchDashboard}
              />
            </Paper>
          </Grid>
        )}

        {view === 'cards' &&
          visibleLocations.map((base) => (
            <Grid item xs={12} md={6} xl={4} key={base._id} display="flex">
              <LocationCard base={base} />
            </Grid>
          ))}
      </Grid>

      <UpgradeRequired
        open={openAdd && limitReached}
        onClose={() => setOpenAdd(false)}
        requiredTier={
          limitInfo?.subscriptionTier === 1 ? 'Premium or Enterprise' : 'Custom'
        }
        title="Base Location Limit Reached"
        customMessage={`Your current subscription allows ${limitInfo.limit} Base Locations`}
        customHint={
          limitInfo.subscriptionTier === 3
            ? 'Contact AmbuNet Support to discuss additional Base Locations'
            : 'Upgrade your plan to add additional Base Locations'
        }
        benefits={
          limitInfo.subscriptionTier === 1
            ? [
                `Premium: ${premiumBaseLocations} Base Locations`,
                `Enterprise: ${enterpriseBaseLocations} Base Locations`,
                'Contact AmbuNet support if you require more',
              ]
            : limitInfo.subscriptionTier === 2
              ? [
                  `Enterprise: ${enterpriseBaseLocations} Base Locations`,
                  'Contact AmbuNet support if you require more',
                ]
              : null
        }
      />
      <BigModal
        open={openAdd && !limitReached}
        autoWidth
        onClose={() => setOpenAdd(false)}
      >
        <BaseLocationForm
          onClose={() => setOpenAdd(false)}
          onSaved={handleSaved}
        />
      </BigModal>
    </Box>
  )
}

export default BaseLocationsDashboard
